import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "melodies-source/Modal";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { PhoneMFA } from "./PhoneMFA";
import { TotpMFA } from "./TotpMFA";
import { SelectMFA } from "./SelectMFA";
import useMfaInfo, { MFA_INFO_QUERY_KEY } from "hooks/useMfaInfo";
import { useQueryClient } from "react-query";
import { Spinner } from "melodies-source/Spinner";
import { FactorDetail } from "./FactorDetail";

interface Props {
  parent?: string;
  onClose?: () => void;
  onSuccess?: () => void;
  withModal?: boolean;
}

export enum Step {
  SelectMFA,
  FactorDetail,
  SetupPhone,
  SetupTOTP,
}

interface Context {
  setStep: (step: Step) => void;
  onClose: () => void;
  onSuccess: () => void;
}

const SetupMFAContext = React.createContext(undefined);

export const SetupMFA: React.FC<Props> = ({
  parent,
  onClose,
  onSuccess,
  withModal = true,
}) => {
  const { mfaInfo, isMfaEnabled, loading: userLoading } = useMfaInfo();
  const [step, setStep] = useState(Step.SelectMFA);
  const history = useHistory();
  const queryClient = useQueryClient();

  const handleClose = () =>
    parent ? history.push(parent) : setStep(Step.SelectMFA);

  // Enrolling a factor is not reflected on the client automatically, so
  // invalidate the MFA lookup to refresh any consumers (e.g. the Account
  // button label) before running the caller's success handler.
  const handleSuccess = () => {
    queryClient.invalidateQueries([MFA_INFO_QUERY_KEY]);
    (onSuccess || handleClose)?.();
  };

  let screen = null;

  switch (step) {
    case Step.SelectMFA: {
      screen = <SelectMFA />;
      break;
    }
    case Step.FactorDetail: {
      screen = <FactorDetail mfaInfo={mfaInfo} />;
      break;
    }
    case Step.SetupPhone: {
      screen = <PhoneMFA />;
      break;
    }
    case Step.SetupTOTP: {
      screen = <TotpMFA />;
      break;
    }
  }

  const value = useMemo(
    () => ({
      setStep,
      onClose: onClose || handleClose,
      onSuccess: handleSuccess,
    }),
    [],
  );

  useEffect(() => {
    if (mfaInfo?.length === 1) {
      setStep(Step.FactorDetail);
    }
  }, [mfaInfo]);

  const content = (
    <SetupMFAContext.Provider value={value}>
      <Container>{userLoading ? <Spinner /> : screen}</Container>
    </SetupMFAContext.Provider>
  );

  return !withModal ? (
    content
  ) : (
    <Modal
      header={isMfaEnabled ? "MFA Details" : "Set up MFA"}
      withCloseIcon={true}
      isOpen={true}
      onClose={handleClose}
    >
      {content}
    </Modal>
  );
};

export const useSetupMFAContext = (): Context => {
  const context = useContext(SetupMFAContext);

  if (context === undefined) {
    throw new Error("useSetupMFAContext must be used within a SetupMFAContext");
  }

  return context;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
